import { Product } from '@components/ui/Product';
import { LineBatchMeta } from '@shared/components/ui/LineBatchMeta';
import type { BatchSummary } from '@shared/models/BatchSummary';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Location } from '../../models/PreparationModels';
import type { PreparationLineWithProduct } from '../../hooks/usePreparationQuery';
import { resolvePickingBatchAtLocation } from '../../utils/resolvePickingBatchAtLocation';
import { hasRenderableLineBatchMeta } from '@shared/utils/sanitizeBatchDisplay';

interface ActiveLocationSectionProps {
  location: Location;
  products: PreparationLineWithProduct[];
  onGoBack?: () => void;
}

function lineBatchParts(
  scannedLocation: Location,
  prod: PreparationLineWithProduct,
): { resolved: NonNullable<ReturnType<typeof resolvePickingBatchAtLocation>> } | null {
  const r = resolvePickingBatchAtLocation(prod, scannedLocation);
  if (!r) return null;
  return { resolved: r };
}

export const ActiveLocationSection: React.FC<ActiveLocationSectionProps> = ({ location, products, onGoBack }) => {
  const { t } = useTranslation('pickingModule');

  const linesUi = useMemo(() => {
    return products.map((line) => {
      const rb = lineBatchParts(location, line);
      const summary: BatchSummary | undefined =
        rb != null
          ? {
              id: rb.resolved.batchId,
              batch_number: rb.resolved.batch?.batch_number ?? undefined,
              expiry_date: rb.resolved.batch?.expiry_date ?? undefined,
            }
          : undefined;
      return { line, rb, summary };
    });
  }, [location, products]);

  if (!location || products.length === 0) {
    return null;
  }

  return (
    <div className="bg-background border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800">
            <span className="font-semibold">{location.code}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            {products.length} producto{products.length !== 1 ? 's' : ''}
          </div>
        </div>

        {onGoBack && (
          <button
            type="button"
            onClick={onGoBack}
            className="text-muted-foreground hover:text-foreground p-2 rounded-lg hover:bg-muted transition-colors"
            title={t('active_location.change_location', { defaultValue: 'Cambiar ubicación' })}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="space-y-3">
        {linesUi.map(({ line, rb, summary }) => {
          const remainingQty = line.qty - line.qty_done;
          const isCompleted = line.qty_done >= line.qty;

          return (
            <div
              key={line.id}
              className={`rounded-lg border p-3 transition-colors ${
                isCompleted
                  ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                  : 'bg-muted/30 border-border/50 hover:bg-muted/50'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2 min-w-0">
                  <Product
                    id={line.product_id}
                    src={line.product?.image || ''}
                    name={line.product?.name || `Producto ID: ${line.product_id}`}
                    code={line.product?.code}
                    acronym={line.product?.name?.charAt(0).toUpperCase()}
                    showImageModal={true}
                    enableLineClamp={true}
                  />
                  {rb &&
                  summary &&
                  hasRenderableLineBatchMeta({
                    batch: summary,
                    batchId: rb.resolved.batchId,
                    hideNumericIdFallback: true,
                  }) ? (
                    <div className="pl-10 sm:pl-11 pt-1">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        {t('quantity_confirm.batch_heading')}
                      </p>
                      <LineBatchMeta
                        batch={summary}
                        batchId={rb.resolved.batchId}
                        hideNumericIdFallback
                        i18nNs="pickingModule"
                        lotLabelKey="quantity_confirm.batch_label"
                        expiryLabelKey="quantity_confirm.expiry_label"
                      />
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center flex-shrink-0 pt-1">
                  {isCompleted ? (
                    <span className="text-green-600 dark:text-green-400 text-sm font-medium whitespace-nowrap">
                      ✓ Completado
                    </span>
                  ) : (
                    <div className="text-center">
                      <div className="font-semibold text-lg text-foreground">{remainingQty}</div>
                      <div className="text-xs text-muted-foreground">
                        {t('active_location.pending', { defaultValue: 'pendiente' })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-border/50">
        <div className="text-xs text-muted-foreground text-center">{t('active_location.scan_hint')}</div>
      </div>
    </div>
  );
};
